import { useState } from 'react'
import { useForm } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { FormationEntityOption } from '@/types'
import { Check, ArrowRight, Clock, DollarSign } from 'lucide-react'

type CountryOption = { id: string; code: string; name: string }
type StateOption = { id: string; code: string; name: string; country_id: string }

type Props = {
  countries: CountryOption[]
  states: StateOption[]
  entities: FormationEntityOption[]
}

export default function FormationsNew({ countries, states, entities }: Props) {
  const basePath = useBasePath()
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedEntityId, setSelectedEntityId] = useState('')

  const countryEntities = entities.filter(e => e.country_id === selectedCountryId)
  const entitiesUseStates = countryEntities.some(e => e.state_id)
  const countryStates = entitiesUseStates ? states.filter(s => s.country_id === selectedCountryId) : []
  const hasStates = countryStates.length > 0

  const filteredEntities = countryEntities.filter(e => {
    if (!selectedCountryId) return false
    if (hasStates && !selectedStateId) return false
    if (hasStates && selectedStateId && e.state_id && e.state_id !== selectedStateId) return false
    return true
  })

  const selectedEntity = entities.find(e => e.id === selectedEntityId)

  const form = useForm({
    entity_id: '',
    legal_name: '',
    assumed_name: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`${basePath}/formations`)
  }

  return (
    <DashboardLayout title="New Formation" breadcrumbs={[{ label: 'Formations', href: 'formations' }, { label: 'New Formation' }]}>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Start Your Business</h1>
        <p className="text-sm text-muted-foreground">Choose your jurisdiction and business type</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Jurisdiction */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
            <h2 className="text-base font-medium text-foreground">Jurisdiction</h2>
          </div>

          <div className={`grid gap-3 ${hasStates ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Country</Label>
              <Combobox
                options={countries.map(c => ({ value: c.id, label: c.name }))}
                value={selectedCountryId}
                onValueChange={(value) => {
                  setSelectedCountryId(value)
                  setSelectedStateId('')
                  setSelectedEntityId('')
                  form.setData('entity_id', '')
                }}
                placeholder="Select country..."
                searchPlaceholder="Search countries..."
                emptyMessage="No countries found."
              />
            </div>

            {hasStates && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">State</Label>
                <Combobox
                  options={countryStates.map(s => ({ value: s.id, label: s.name }))}
                  value={selectedStateId}
                  onValueChange={(value) => {
                    setSelectedStateId(value)
                    setSelectedEntityId('')
                    form.setData('entity_id', '')
                  }}
                  placeholder="Select state..."
                  searchPlaceholder="Search states..."
                  emptyMessage="No states found."
                />
              </div>
            )}
          </div>
        </section>

        {/* Entity Type */}
        {filteredEntities.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
              <h2 className="text-base font-medium text-foreground">Business Type</h2>
            </div>

            <div className="space-y-1.5">
              {filteredEntities.map((entity) => {
                const isSelected = selectedEntityId === entity.id
                return (
                  <button
                    key={entity.id}
                    type="button"
                    onClick={() => {
                      setSelectedEntityId(entity.id)
                      form.setData('entity_id', entity.id)
                    }}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${
                      isSelected ? 'border-foreground bg-muted' : 'border-border hover:border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{entity.name}</span>
                          {isSelected && <Check className="w-4 h-4 text-foreground" />}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {entity.processing_days_min}-{entity.processing_days_max} days
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {formatCurrency(entity.total_cents)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
            {form.errors.entity_id && <p className="mt-3 text-[12px] text-destructive">{form.errors.entity_id}</p>}
          </section>
        )}

        {/* Company Details */}
        {selectedEntity && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
              <h2 className="text-base font-medium text-foreground">Company Details</h2>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="legal_name">Company Name</Label>
                <Input id="legal_name" type="text" value={form.data.legal_name} onChange={e => form.setData('legal_name', e.target.value)} placeholder="Enter legal name" />
                {form.errors.legal_name && <p className="text-[12px] text-destructive">{form.errors.legal_name}</p>}
                <p className="text-xs text-muted-foreground">The official legal name for state documents</p>
              </div>

              {selectedEntity.name_options.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Name Suffix</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedEntity.name_options.map((opt) => {
                      const isActive = form.data.assumed_name === opt.suffix
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => form.setData('assumed_name', isActive ? '' : opt.suffix)}
                          className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                            isActive ? 'border-foreground bg-muted font-medium' : 'border-border hover:border-foreground/40'
                          }`}
                        >
                          {opt.label}
                        </button>
                      )
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {form.data.assumed_name
                      ? `Full name: ${form.data.legal_name || '...'} ${form.data.assumed_name}`
                      : 'Select a suffix for your company name'}
                  </p>
                </div>
              )}

              {(!selectedEntity.name_options.length || selectedEntity.name_options.length === 0) && (
                <div className="space-y-1.5">
                  <Label htmlFor="assumed_name">DBA Name <span className="text-muted-foreground font-normal">(Optional)</span></Label>
                  <Input id="assumed_name" type="text" value={form.data.assumed_name} onChange={e => form.setData('assumed_name', e.target.value)} placeholder="Doing Business As" />
                  <p className="text-xs text-muted-foreground">The name customers will see</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Review & Submit */}
        {selectedEntity && form.data.legal_name && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">4</span>
              <h2 className="text-base font-medium text-foreground">Review & Submit</h2>
            </div>

            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{selectedEntity.name}</span>
                  <span className="font-medium">{formatCurrency(selectedEntity.total_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Service Fee</span>
                  <span>{formatCurrency(selectedEntity.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>State Filing Fee</span>
                  <span>{formatCurrency(selectedEntity.state_fee_cents)}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(selectedEntity.total_cents)}</span>
                </div>
              </div>

              <Button type="submit" className="w-full h-9 text-sm" disabled={form.processing}>
                {form.processing ? 'Creating...' : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </form>
    </DashboardLayout>
  )
}
